import { connect, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { VideoCategoryProps } from "../category/store/actionType";
import { VideoSeriesProps } from "../series/store/actionType";
import Loading from "../components/Loading";
import BreadcrumbFolderLayout from "../components/BreadcrumbFolderLayout";
import { AddFolderButton, SmallScreenModel } from "components";
import { useEffect, useState } from "react";
import VideoDetailDisplay from "./page/components/VideoDetailsDisplay";
import CreateVideo from "./page/HandleUploadVideo";
import { VideoGroupProps } from "../group/store/actionType";
import {
  fetchVideoDetails,
  resetVideo,
} from "modules/video/video_detail/store/action";
import { VideoSeasonProps } from "../season/store/actionType";
import DeleteVideo from "./page/DeleteVideo";
import HandleCreateVideo from "./page/HandleCreateVideo";
import { Authorities } from "props/authoritries";
import { useUser } from "utils/helpers/UserContext";
import { SuperAdminStatusEnum } from '../../../props/enum';

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type StateProps = ReturnType<typeof mapStateToProps>;
type Props = DispatchProps & StateProps;
const VideoDetail: React.FC<Props> = ({
  videoDetailsState,
  onFetchVideoDetails,
  onResetVideo,
}) => {
  const [modalMode, setModalMode] = useState<"create" | "delete" | null>(null);
  const navigate = useNavigate();
  const [enableEdit, setEndableEdit] = useState(false);
  const closeModal = () => {
    setModalMode(null);
  };
  //group
  const { groupId } = useParams<{ groupId: string }>();
  const groupsState = useSelector(
    (state: any) => state.videoGroups.videoGroupsState
  );
  const currentGroup = groupsState.groups?.filter(
    (group: VideoGroupProps) => group.id === groupId
  )[0];
  //category
  const { categoryId } = useParams<{ categoryId: string }>();
  const category_id: string = categoryId!;
  const categoryState = useSelector(
    (state: any) => state.videoCategories.videoCategoriesState
  );
  const currentCategory = categoryState.categories?.filter(
    (category: VideoCategoryProps) => category.id === categoryId
  )[0];
  //series
  const { seriesId } = useParams<{ seriesId: string }>();
  const seriesState = useSelector(
    (state: any) => state.videoSeries.videoSeriesState
  );
  const currentSeries = seriesState.video_series?.filter(
    (series: VideoSeriesProps) => series.id === seriesId
  )[0];
  //season
  const { seasonId } = useParams<{ seasonId: string }>();
  const seasonState = useSelector(
    (state: any) => state.videoSeasons?.videoSeasonsState
  );
  const currentSeason = seasonState.video_seasons?.filter(
    (season: VideoSeasonProps) => season.id === seasonId
  )[0];
  //episode
  const { episodeId } = useParams<{ episodeId: string }>();
  const episode_id: string = episodeId!;
  const episodeState = useSelector(
    (state: any) => state.videoEpisodes?.videoEpisodeState
  );
  const season_folder_id = currentSeason?.folder_id;
  const currentEpisode = episodeState.video_episode?.filter(
    (episode: VideoSeasonProps) => episode.id === episodeId
  )[0];
  const redirectToGroup = () => {
    navigate(`/video`);
  };
  const redirectToCategory = () => {
    navigate(`/video/${groupId}`);
  };
  const redirectToSeries = () => {
    navigate(`/video/${groupId}/${categoryId}`);
  };
  const redirectToSeason = () => {
    navigate(`/video/${groupId}/${categoryId}/${seriesId}`);
  };
  const redirectToEpisode = () => {
    navigate(`/video/${groupId}/${categoryId}/${seriesId}/${seasonId}`);
  };

  const breadcrumbItem = [
    {
      group: "Group",
      root: "Root",
      active: true,
      onClick: () => redirectToGroup(),
    },
    {
      group: currentGroup?.name,
      root: "Group",
      active: true,
      onClick: () => redirectToCategory(),
    },
    {
      group: currentCategory?.name,
      root: "Category",
      active: true,
      onClick: () => redirectToSeries(),
    },
    {
      group: currentSeries?.title,
      root: "Series",
      active: true,
      onClick: () => redirectToSeason(),
    },
    {
      group: currentSeason?.title,
      root: "Season",
      active: true,
      onClick: () => redirectToEpisode(),
    },
    {
      group: currentEpisode?.label,
      root: "Video",
    },
  ];
  useEffect(() => {
    onFetchVideoDetails(episodeId ?? "");
  }, [onFetchVideoDetails]);
  useEffect(() => {
    onResetVideo();
  }, [onResetVideo]);
  useEffect(() => {
    if (videoDetailsState?.video_details === null) {
      setEndableEdit(false);
    } else {
      setEndableEdit(true);
    }
  }, [videoDetailsState?.video_details]);
  const { authUser, action, superAdmin } = useUser();
  const isPermitted =
    authUser &&
    (superAdmin === SuperAdminStatusEnum.SuperAdmin  ||
      action?.includes(
        Authorities.CREATE_VIDEO || Authorities.UPDATE_CATEGORY
      ));
  return (
    <div className="h-full overflow-hidden">
      {videoDetailsState && videoDetailsState.isLoading && <Loading />}
      <BreadcrumbFolderLayout
        groupData={videoDetailsState?.video_details?.video}
        breadcrumbItem={breadcrumbItem}
        addFolderButton={
          <>
            {isPermitted && (
              <>
                {enableEdit ? (
                  <AddFolderButton
                    onVideo
                    onClick={() => setModalMode("delete")}
                  >
                    Delete Video
                  </AddFolderButton>
                ) : (
                  <AddFolderButton
                    onVideo
                    onClick={() => setModalMode("create")}
                  >
                    Add Video
                  </AddFolderButton>
                )}
              </>
            )}
          </>
        }
        folders={
          <>
            {videoDetailsState?.video_details ? (
              <VideoDetailDisplay
                videoDetails={videoDetailsState?.video_details}
              />
            ) : (
              <div className="text-center text-gray-500 p-10 text-xl">
                <p>Currently no video detail available.</p>
              </div>
            )}
          </>
        }
      />
      {modalMode && (
        <SmallScreenModel
          showModal={!!modalMode}
          setShowModal={() => setModalMode(null)}
        >
          {modalMode === "delete" ? (
            <DeleteVideo
              closeModal={() => setModalMode(null)}
              videoData={videoDetailsState?.video_details?.video}
            />
          ) : (
            <HandleCreateVideo
              closeModal={() => setModalMode(null)}
              videoData={videoDetailsState?.video_details?.video}
              season_folder_id={season_folder_id}
              episode_id={episodeId?.toString()}
              category_id={categoryId?.toString()}
            />
          )}
        </SmallScreenModel>
      )}
    </div>
  );
};
export const mapStateToProps = (state: any) => ({
  videoDetailsState: state.videoDetails?.videoDetailsState,
});

const mapDispatchToProps = (dispatch: any) => ({
  onFetchVideoDetails: (episode_id: string) =>
    dispatch(fetchVideoDetails(episode_id)),
  onResetVideo: () => dispatch(resetVideo()),
});
export default connect(mapStateToProps, mapDispatchToProps)(VideoDetail);
